import React, { useState, useEffect } from 'react';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { APPLICATION_ID, HAS_INSTANT_DISCOUNT, USE_PARITY } from '../../../../constants';

import gtm from '../../../../utils/gtmUtils';
import { convertDate } from '../../../../utils/dateUtils';
import { displayableCashback } from '../../../../services/partner/utils';
import { doDiscount, doDiscountParity } from '../../../../utils/cartUtils';
import { formatAmountCurrency, formatAmountParity } from '../../../../utils';
import { convertHtmlToReactDomWithParams } from '../../../../utils/htmlUtils';

import ButtonComponent from '../../../button/ButtonComponent';
import EbonSelectorPickerComponent from './EbonSelectorPickerComponent';

import { SelectorLeftContainer } from './EbonSelectorLeftContainerStyle';

const EbonSelectorLeftContainer = ({
    partner,
    cart,
    addItemCart,
    cartTotalQuantity,
    basketLimit,
    isBasketLimitReached,
    getBasketLimitMessage,
    loginRequired,
    showLoginModal,
    i18n,
    i18nCurrency,
    ebons,
    CASHBACK
}) => {
    const [selectedEbon, setSelectedEbon] = useState({});
    const [quantity, setQuantity] = useState(0);
    const [inputValue, setInputValue] = useState('');
    const [error, setError] = useState('');
    const [disable, setDisable] = useState(false);
    const { ebonDiscount = {} } = ebons;

    const discount = USE_PARITY ? ebonDiscount.parity : ebonDiscount.cashback;
    const fixed = discount.fixed.value;
    const percent = discount.percent.value;
    const cashback = displayableCashback(percent, fixed);
    const amountCashback = fixed > 0 ? fixed : percent;

    const hasFreeFacial = ebons.freeFacialValue !== undefined;

    useEffect(() => {
        if (hasFreeFacial) {
            if (cart && cart.length > 0) {
                setDisable(true);
            } else {
                setDisable(false);
            }
        }
    }, [cart]);

    useEffect(() => {
        const initQuantity =
            selectedEbon && selectedEbon.facialValue
                ? selectedEbon.facialValue.quantityQuery || 1
                : 1;
        setQuantity(initQuantity);
    }, [selectedEbon]);

    useEffect(() => {
        setSelectedEbon({});
    }, [partner]);

    useEffect(() => {}, [selectedEbon]);

    const handleAddItem = (facialValue, quantityQuery, quantityAvailable) => {
        if (!error) {
            addItemCart({
                facialValue: facialValue || selectedEbon.facialValue,
                quantityQuery: quantityQuery || quantity,
                quantityAvailable: quantityAvailable,
                partnerId: ebons.ebonDiscount.partnerId
            });
        }
    };

    const handleQuantity = e => {
        if (e === '-') {
            if (quantity > 1) {
                setQuantity(quantity - 1);
            }
        } else if (e === '+') {
            if (
                quantity < selectedEbon.quantityAvailable &&
                (!basketLimit || cartTotalQuantity + quantity < basketLimit)
            ) {
                setQuantity(quantity + 1);
            }
        }
    };

    const getLabelResume = () => {
        switch (true) {
            case HAS_INSTANT_DISCOUNT:
                return quantity > 1
                    ? i18n.RESUM_EBON_INSTANT_DISCOUNT_PLURAL
                    : i18n.RESUM_EBON_INSTANT_DISCOUNT;
            case USE_PARITY:
                switch (APPLICATION_ID) {
                    default:
                        return quantity > 1
                            ? i18n.RESUM_EBON_PARITY_DISCOUNT_PLURAL
                            : i18n.RESUM_EBON_PARITY_DISCOUNT;
                }
            default:
                return quantity > 1
                    ? i18n.RESUM_EBON_CASHBACK_DISCOUNT_PLURAL
                    : i18n.RESUM_EBON_CASHBACK_DISCOUNT;
        }
    };

    const onAdd = event => {
        if (!loginRequired) {
            gtm.event('add_to_cart', {
                ...selectedEbon,
                quantity
            });
            handleAddItem(selectedEbon.facialValue, quantity, selectedEbon.quantityAvailable);
            setSelectedEbon({});
            setInputValue('');
        } else {
            showLoginModal(event);
        }
    };

    return (
        <SelectorLeftContainer>
            <p className="valueTips">
                <span>{i18n.VALUE}</span>
                {hasFreeFacial ? i18n.VALUE_FREE : i18n.VALUE_FIXED}
            </p>

            <div className="ebon-selector-container">
                <EbonSelectorPickerComponent
                    ebons={ebons}
                    selectedEbon={selectedEbon}
                    setSelectedEbon={setSelectedEbon}
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    error={error}
                    setError={setError}
                    disable={disable}
                />

                <div>
                    {!hasFreeFacial && (
                        <p className="availability">
                            {selectedEbon.expirationDate &&
                                convertHtmlToReactDomWithParams(
                                    i18n.EBON_AVAILABILITY,
                                    convertDate(selectedEbon.expirationDate)
                                )}
                        </p>
                    )}

                    <div className="quantity-selector">
                        <p>{i18n.QUANTITY}</p>
                        <div className="quantity-button">
                            <button onClick={() => handleQuantity('-')} disabled={quantity <= 1}>
                                -
                            </button>
                            <div>{quantity}</div>
                            <button
                                onClick={() => handleQuantity('+')}
                                disabled={
                                    quantity >= selectedEbon.quantityAvailable ||
                                    (basketLimit && cartTotalQuantity + quantity >= basketLimit)
                                }>
                                +
                            </button>
                        </div>
                    </div>

                    {!hasFreeFacial && (selectedEbon.quantityAvailable === 1 || basketLimit) && (
                        <div className="error-area">
                            {selectedEbon.quantityAvailable === 1 && (
                                <p>
                                    {convertHtmlToReactDomWithParams(
                                        i18n.LIMITED_REMAINING_EBON,
                                        selectedEbon.quantityAvailable,
                                        selectedEbon.quantityAvailable > 1 ? 's' : ''
                                    )}
                                </p>
                            )}
                            {isBasketLimitReached() && (
                                <p>
                                    {convertHtmlToReactDomWithParams(
                                        getBasketLimitMessage(),
                                        basketLimit,
                                        basketLimit > 1 ? 's' : ''
                                    )}
                                </p>
                            )}
                        </div>
                    )}

                    <div className="resum-area">
                        <p>
                            {!error &&
                                selectedEbon.facialValue &&
                                convertHtmlToReactDomWithParams(
                                    getLabelResume(),
                                    quantity,
                                    formatAmountCurrency(selectedEbon.facialValue),
                                    cashback,
                                    USE_PARITY
                                        ? formatAmountParity(
                                              doDiscountParity(
                                                  amountCashback,
                                                  quantity,
                                                  selectedEbon.facialValue
                                              )
                                          )
                                        : formatAmountCurrency(
                                              doDiscount(
                                                  amountCashback,
                                                  quantity,
                                                  selectedEbon.facialValue
                                              )
                                          ),
                                    i18nCurrency.PARITY_CONDITION,
                                    CASHBACK
                                )}
                        </p>
                    </div>

                    <div className="button-add">
                        <ButtonComponent
                            disabled={
                                isEmpty(selectedEbon) ||
                                !!error ||
                                disable ||
                                isBasketLimitReached()
                            }
                            onClick={e => onAdd(e)}
                            cssClass={`${!selectedEbon.quantityAvailable && 'none-selected'}`}
                            color="main"
                            text={i18n.ADD_BASKET}
                        />
                    </div>
                </div>
            </div>
        </SelectorLeftContainer>
    );
};

EbonSelectorLeftContainer.propTypes = {
    partner: PropTypes.object.isRequired,
    cart: PropTypes.array.isRequired,
    addItemCart: PropTypes.func.isRequired,
    cartTotalQuantity: PropTypes.number,
    basketLimit: PropTypes.number,
    isBasketLimitReached: PropTypes.func,
    getBasketLimitMessage: PropTypes.func,
    loginRequired: PropTypes.bool.isRequired,
    showLoginModal: PropTypes.func.isRequired
};

const mapStateToProps = state => {
    const cashbackWordings = state.i18n.common.CASHBACK;
    const cashback = cashbackWordings[APPLICATION_ID]
        ? cashbackWordings[APPLICATION_ID]
        : cashbackWordings.DEFAULT;

    return {
        i18n: state.i18n.page.MERCHANTCARD.EBON_SELECTOR,
        i18nCurrency: state.i18n.CURRENCY,
        ebons: state.ebon.ebons,
        CASHBACK: cashback
    };
};

export default withRouter(connect(mapStateToProps)(EbonSelectorLeftContainer));



// WEBPACK FOOTER //
// ./src/components/pages/merchantcard/ebonselector/EbonSelectorLeftContainer.js